/**
 * `PerformanceBreakdown` — Task 157.
 *
 * Top-performers table for the performance summary. A level selector
 * (campaign / ad_set / ad) picks which pre-aggregated bucket to show; a sort
 * selector (spend / conversions / ROAS, descending, nulls last) orders the
 * rows. Entity names come from the EF; when a name is null a generic Arabic
 * label is shown — the internal `entity_id` (a UUID) is NEVER rendered as
 * visible text, only used as a React key.
 *
 * Sorting is a presentation concern over an already-aggregated, bounded list
 * (top-N per level from the EF), so it does not violate the panel's
 * "no client-side aggregation" rule — no sums or ratios are recomputed here.
 */

import { useMemo, useState } from 'react';
import type {
  PaidMediaMetricLevel,
  PaidMediaPerformanceBreakdownEntry,
  PaidMediaPerformanceSummaryData,
} from '../../api/types';
import { fmtCount, fmtMoney, fmtPercent, fmtRoas } from './CampaignMetricsPanel';

type SortKey = 'spend' | 'conversions' | 'roas';

// The EF returns a per-sort-key UNION pool (top-N by spend ∪ conversions ∪
// ROAS); we sort by the selected key and show its true top-N.
const DISPLAY_LIMIT = 10;

interface PerformanceBreakdownProps {
  readonly breakdown: PaidMediaPerformanceSummaryData['breakdown'];
}

const LEVELS: ReadonlyArray<{ readonly key: PaidMediaMetricLevel; readonly label: string }> = [
  { key: 'campaign', label: 'الحملة' },
  { key: 'ad_set', label: 'المجموعات الإعلانية' },
  { key: 'ad', label: 'الإعلانات' },
];

const SORTS: ReadonlyArray<{ readonly key: SortKey; readonly label: string }> = [
  { key: 'spend', label: 'الإنفاق' },
  { key: 'conversions', label: 'التحويلات' },
  { key: 'roas', label: 'ROAS' },
];

// Generic fallback when the entity has no resolved name.
const FALLBACK_NAME: Record<PaidMediaMetricLevel, string> = {
  campaign: 'حملة',
  ad_set: 'مجموعة إعلانية',
  ad: 'إعلان',
};

function sortValue(entry: PaidMediaPerformanceBreakdownEntry, key: SortKey): number | null {
  return entry[key];
}

// Descending sort with nulls always last, regardless of direction.
function compareDesc(
  a: PaidMediaPerformanceBreakdownEntry,
  b: PaidMediaPerformanceBreakdownEntry,
  key: SortKey,
): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return bv - av;
}

export function PerformanceBreakdown({ breakdown }: PerformanceBreakdownProps) {
  const [level, setLevel] = useState<PaidMediaMetricLevel>('campaign');
  const [sort, setSort] = useState<SortKey>('spend');

  const activeLevel = LEVELS.find((l) => l.key === level) ?? LEVELS[0];

  const rows = useMemo(() => {
    const source = breakdown[level] ?? [];
    // Copy before sorting — never mutate the prop array. Sort by the selected
    // key, then slice to the true top-N for that key (the pool already
    // contains it — see DISPLAY_LIMIT note).
    return [...source].sort((a, b) => compareDesc(a, b, sort)).slice(0, DISPLAY_LIMIT);
  }, [breakdown, level, sort]);

  return (
    <div dir="rtl">
      <div className="sec-title" style={{ marginBottom: 10 }}>
        <h2>الأفضل أداءً</h2>
        <span className="meta">{activeLevel.label}</span>
      </div>

      <div className="toolbar-row" style={{ gap: 16, marginBottom: 12, alignItems: 'flex-start' }}>
        <div
          role="group"
          aria-label="المستوى"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
        >
          {LEVELS.map((l) => (
            <button
              key={l.key}
              type="button"
              className={`chip${l.key === level ? ' on' : ''}`}
              aria-pressed={l.key === level}
              onClick={() => setLevel(l.key)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div
          role="group"
          aria-label="الترتيب حسب"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
        >
          <span className="lbl" style={{ alignSelf: 'center' }}>
            ترتيب حسب
          </span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`chip${s.key === sort ? ' on' : ''}`}
              aria-pressed={s.key === sort}
              onClick={() => setSort(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="body" style={{ color: 'var(--muted)' }}>
          لا توجد عناصر لعرضها لهذا المستوى ضمن النطاق المحدد.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>الإنفاق</th>
                <th>التحويلات</th>
                <th>ROAS</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.entity_id}>
                  <td className="name">{entry.name ?? FALLBACK_NAME[entry.level]}</td>
                  <td className="num">{fmtMoney(entry.spend, null)}</td>
                  <td className="num">{fmtCount(entry.conversions)}</td>
                  <td className="num">{fmtRoas(entry.roas)}</td>
                  <td className="num">{fmtPercent(entry.ctr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
